import React, { useState, useEffect } from "react";
import { supabase } from "../../utils/supabaseClient";
import { withTimeout } from "../../utils/withTimeout";

const getInputClass = (isDarkMode, field, errors) =>
  `w-full p-3 h-12 rounded-xl border font-bold outline-none transition-colors ${
    errors[field] ? "border-rose-400" : ""
  } ${
    isDarkMode
      ? "bg-slate-900 border-slate-700 focus:border-sky-500"
      : "bg-slate-50 border-slate-200 focus:border-[#132B43]"
  }`;

export default function CapitalForm({
  onClose,
  userId,
  onSaveSuccess,
  isDarkMode,
  editingItem,
  setNotification,
  themeConfig,
}) {
  const [formData, setFormData] = useState({
    type: "Deposit",
    amount_usd: "",
    date_transaction: new Date().toISOString().split("T")[0],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!editingItem) return;
    setFormData({
      type: editingItem.type || editingItem.Type || "Deposit",
      amount_usd:
        editingItem.amount_usd ||
        editingItem.Amount_USD ||
        editingItem.amount ||
        editingItem.Amount ||
        "",
      date_transaction: editingItem.date_transaction
        ? editingItem.date_transaction.split("T")[0]
        : editingItem.Date_Transaction
        ? editingItem.Date_Transaction.split("T")[0]
        : new Date().toISOString().split("T")[0],
    });
  }, [editingItem]);

  const validate = () => {
    const e = {};
    if (!formData.amount_usd || Number(formData.amount_usd) <= 0)
      e.amount_usd = "กรุณากรอกจำนวนเงินที่มากกว่า 0";
    if (!formData.date_transaction)
      e.date_transaction = "กรุณาเลือกวันที่ทำรายการ";
    return e;
  };

  const handleSubmit = async () => {
    if (isSaving) return;

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setIsSaving(true);

    try {
      const payload = {
        user_id:          userId,
        type:             formData.type,
        amount_usd:       Number(formData.amount_usd),
        date_transaction: formData.date_transaction,
        last_updated:     new Date().toISOString(),
      };
      if (editingItem?.id) payload.id = editingItem.id;

      const { data, error } = await withTimeout(
        supabase.from("capitals").upsert(payload).select().single()
      );

      if (error) throw error;

      if (setNotification) {
        const isEdit    = !!editingItem?.id;
        const typeText  = formData.type === "Deposit" ? "Deposit" : "Withdrawal";
        setNotification(`${isEdit ? "อัปเดต" : "บันทึก"}${typeText}แล้ว`);
      }

      onClose();
      if (onSaveSuccess && data) onSaveSuccess(data);
    } catch (e) {
      console.error("Save Capital Error:", e);
      if (setNotification) {
        if (e?.message === "TIMEOUT") {
          setNotification("ใช้เวลานานเกินกำหนด กรุณาตรวจการเชื่อมต่อแล้วลองอีกครั้ง");
        } else {
          setNotification("บันทึกเงินทุนไม่สำเร็จ ข้อมูลที่กรอกไว้ยังอยู่ครบ");
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass = (field) => getInputClass(isDarkMode, field, errors);

  return (
    <div
      className={`p-6 md:p-8 rounded-xl border max-w-md w-full shadow-sm ${
        isDarkMode
          ? "bg-[#1E1F20] border-slate-700 text-white"
          : "bg-white border-slate-200 text-slate-900"
      }`}
    >
      <h3 className="text-2xl font-black mb-1">บันทึกเงินทุน</h3>
      <p className="text-[13px] font-bold opacity-70 uppercase tracking-widest mb-6">
        บันทึกเฉพาะยอดเงินขาเข้า - ออกจากไทยเท่านั้น
      </p>

      <div className="space-y-4">

        {/* Type */}
        <div>
          <label className="text-[11px] font-bold uppercase ml-1 opacity-50">ประเภท</label>
          <select
            className={inputClass("type")}
            value={formData.type}
            onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value }))}
          >
            <option value="Deposit">ฝากเงิน (Deposit)</option>
            <option value="Withdrawal">ถอนเงิน (Withdrawal)</option>
          </select>
        </div>

        {/* Amount */}
        <div>
          <label className="text-[11px] font-bold uppercase ml-1 opacity-50">จำนวนเงิน (USD)</label>
          <input
            type="number"
            step="any"
            placeholder="0.00"
            className={inputClass("amount_usd")}
            value={formData.amount_usd}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, amount_usd: e.target.value }));
              if (errors.amount_usd) setErrors((prev) => ({ ...prev, amount_usd: null }));
            }}
          />
          {errors.amount_usd && (
            <p className="text-rose-400 text-[11px] font-bold mt-1 ml-1">{errors.amount_usd}</p>
          )}
        </div>

        {/* Date */}
        <div>
          <label className="text-[11px] font-bold uppercase ml-1 opacity-50">วันที่ทำรายการ</label>
          <input
            type="date"
            className={inputClass("date_transaction")}
            value={formData.date_transaction}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, date_transaction: e.target.value }));
              if (errors.date_transaction) setErrors((prev) => ({ ...prev, date_transaction: null }));
            }}
          />
          {errors.date_transaction && (
            <p className="text-rose-400 text-[11px] font-bold mt-1 ml-1">{errors.date_transaction}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={onClose}
            className={`flex-1 p-3.5 h-12 rounded-xl font-bold uppercase text-xs transition-colors ${
              isDarkMode
                ? "bg-slate-800 text-slate-400 hover:bg-slate-700"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            ยกเลิก
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className={`flex-1 p-3.5 h-12 rounded-xl text-white font-bold uppercase text-xs disabled:opacity-50 transition-colors ${
              isDarkMode
                ? "bg-sky-500 hover:bg-sky-600"
                : "bg-[#132B43] hover:bg-[#132B43]/90"
            }`}
          >
            {isSaving ? "กำลังบันทึก..." : "บันทึก"}
          </button>
        </div>

      </div>
    </div>
  );
}
